import { ReactNode, useMemo, useState } from 'react';
import { FieldError } from 'react-hook-form';

import { Label as InputLabel, Error as InputError } from './Input';
import CheckBox from './CheckBox';

export type MultiSelectOption = {
    label: string;
    value: string;
    description?: string;
    suboptions?: MultiSelectOption[];
    disabled?: boolean;
};

export type MultiSelectListProps = {
    label?: string;
    options: MultiSelectOption[];
    value?: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
    allSelected?: boolean;
    error?: FieldError;
    errorMessage?: string;
    helperText?: string;
    placeholder?: string;
    noOptionsLabel?: string;
    noMatchesLabel?: string;
    selectedLabel?: string;
    isLoading?: boolean;
};

const defaultPlaceholder = 'Search';
const defaultNoOptionsLabel = 'No options available.';
const defaultNoMatchesLabel = 'No matches for your search.';
const defaultSelectedLabel = 'No items selected yet.';

export const MultiSelectList = ({
    label,
    options,
    value = [],
    onChange,
    disabled = false,
    allSelected = false,
    error,
    errorMessage,
    helperText,
    placeholder = defaultPlaceholder,
    noOptionsLabel = defaultNoOptionsLabel,
    noMatchesLabel = defaultNoMatchesLabel,
    selectedLabel = defaultSelectedLabel,
    isLoading = false,
}: MultiSelectListProps) => {
    const [search, setSearch] = useState('');
    const selectedSet = useMemo(() => new Set(value), [value]);
    const { flattenedOptions, descendantMap } = useMemo(() => {
        type FlattenedOption = {
            option: MultiSelectOption;
            depth: number;
            ancestors: string[];
        };

        const result: FlattenedOption[] = [];
        const descendants = new Map<string, string[]>();

        const traverse = (
            current: MultiSelectOption[],
            depth: number,
            ancestors: string[],
        ): string[] => {
            const collected: string[] = [];

            current.forEach(option => {
                result.push({
                    option,
                    depth,
                    ancestors,
                });

                const childDescendants = traverse(
                    option.suboptions ?? [],
                    depth + 1,
                    [...ancestors, option.value],
                );
                descendants.set(option.value, childDescendants);
                collected.push(option.value, ...childDescendants);
            });

            return collected;
        };

        traverse(options, 0, []);

        return {
            flattenedOptions: result,
            descendantMap: descendants,
        };
    }, [options]);

    const valueToNode = useMemo(() => {
        const map = new Map<
            string,
            { depth: number; ancestors: string[]; option: MultiSelectOption }
        >();
        flattenedOptions.forEach(entry => {
            map.set(entry.option.value, {
                depth: entry.depth,
                ancestors: entry.ancestors,
                option: entry.option,
            });
        });
        return map;
    }, [flattenedOptions]);

    const totalOptions = flattenedOptions.length;

    const normalizedSearch = search.trim().toLowerCase();

    const visibleValues = useMemo(() => {
        if (!normalizedSearch) {
            return null;
        }
        const matches = new Set<string>();

        flattenedOptions.forEach(({ option, ancestors }) => {
            const labelMatch = option.label
                .toLowerCase()
                .includes(normalizedSearch);
            const descriptionMatch = option.description
                ?.toLowerCase()
                .includes(normalizedSearch);
            if (labelMatch || descriptionMatch) {
                matches.add(option.value);
                const descendants = descendantMap.get(option.value) ?? [];
                descendants.forEach(descendant => matches.add(descendant));
                ancestors.forEach(ancestor => matches.add(ancestor));
            }
        });

        return matches;
    }, [flattenedOptions, descendantMap, normalizedSearch]);

    const toggleValue = (optionValue: string) => {
        const optionEntry = valueToNode.get(optionValue);
        if (optionEntry?.option.disabled) {
            return;
        }
        const next = new Set(selectedSet);
        if (next.has(optionValue)) {
            next.delete(optionValue);
        } else {
            next.add(optionValue);
        }

        onChange(Array.from(next));
    };

    const removeValue = (optionValue: string) => {
        const optionEntry = valueToNode.get(optionValue);
        if (optionEntry?.option.disabled) {
            return;
        }
        if (!selectedSet.has(optionValue)) {
            return;
        }
        const next = value.filter(current => current !== optionValue);
        onChange(next);
    };

    const renderedSelectedOptions = useMemo(() => {
        if (!value.length) {
            return [];
        }
        return value
            .map(optionValue => valueToNode.get(optionValue))
            .filter(
                (
                    entry,
                ): entry is {
                    depth: number;
                    ancestors: string[];
                    option: MultiSelectOption;
                } => Boolean(entry),
            )
            .filter(entry =>
                entry.ancestors.every(ancestor => !selectedSet.has(ancestor)),
            )
            .map(entry => entry.option);
    }, [value, valueToNode, selectedSet]);

    const showOptionsEmptyState = !isLoading && totalOptions === 0;
    const hasSearch = normalizedSearch.length > 0;
    const hasMatches = !hasSearch || (visibleValues?.size ?? 0) > 0;
    const showFilteredEmptyState = !isLoading && hasSearch && !hasMatches;

    const renderOptionTree = (
        optionList: MultiSelectOption[],
        depth = 0,
        ancestors: string[] = [],
    ): ReactNode =>
        optionList.map(option => {
            if (visibleValues && !visibleValues.has(option.value)) {
                return null;
            }

            const nextAncestors = [...ancestors, option.value];
            const isAncestorSelected = ancestors.some(ancestor =>
                selectedSet.has(ancestor),
            );
            const isSelected = selectedSet.has(option.value);
            const isOptionDisabled =
                disabled || isAncestorSelected || option.disabled;
            const hasChildren = Boolean(option.suboptions?.length);
            const indentation = depth * 16;

            return (
                <div key={option.value} className="space-y-1">
                    <div
                        className={`px-3 py-2 transition-colors ${
                            isOptionDisabled
                                ? 'text-gray-400 cursor-default'
                                : 'cursor-pointer hover:bg-vanillaWhite'
                        }`}
                        style={{ paddingLeft: 12 + indentation }}
                        onClick={event => {
                            // The checkbox label handles its own clicks. The row
                            // handler extends that hit area into the surrounding
                            // padding without toggling label clicks twice.
                            if (
                                isOptionDisabled ||
                                (event.target as Element).closest('label')
                            ) {
                                return;
                            }

                            toggleValue(option.value);
                        }}
                    >
                        <CheckBox
                            label={option.label}
                            helperText={option.description}
                            checked={
                                isSelected || isAncestorSelected || allSelected
                            }
                            disabled={isOptionDisabled}
                            onChange={() => {
                                if (isOptionDisabled) {
                                    return;
                                }
                                toggleValue(option.value);
                            }}
                        />
                    </div>
                    {hasChildren && (
                        <div className="ml-4 border-l border-starkWhite pl-2">
                            {renderOptionTree(
                                option.suboptions ?? [],
                                depth + 1,
                                nextAncestors,
                            )}
                        </div>
                    )}
                </div>
            );
        });

    return (
        <div className="space-y-3">
            {label && <InputLabel label={label} />}
            {isLoading && (
                <p className="text-sm text-mongooseGray">
                    {helperText ?? 'Loading…'}
                </p>
            )}
            <div className="rounded-2xl overflow-hidden  border border-starkWhite bg-white shadow-sm">
                <div className="border-b border-starkWhite px-3 py-2">
                    <input
                        className="w-full border-none bg-transparent text-sm focus:outline-none"
                        placeholder={placeholder}
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        disabled={disabled || options.length === 0}
                    />
                </div>
                <div className="max-h-64 overflow-y-auto">
                    {showOptionsEmptyState ? (
                        <p className="p-3 text-sm text-mongooseGray">
                            {noOptionsLabel}
                        </p>
                    ) : showFilteredEmptyState ? (
                        <p className="p-3 text-sm text-mongooseGray">
                            {noMatchesLabel}
                        </p>
                    ) : (
                        renderOptionTree(options)
                    )}
                </div>
            </div>
            {renderedSelectedOptions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {renderedSelectedOptions.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className="flex items-center gap-2 rounded-full border border-brightOrange px-3 py-1 text-xs text-brightOrange"
                            onClick={() => removeValue(option.value)}
                            disabled={disabled || option.disabled}
                        >
                            <span>{option.label}</span>
                            <span aria-hidden="true">×</span>
                        </button>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-mongooseGray">{selectedLabel}</p>
            )}
            {errorMessage && (
                <p className="text-venetianRed text-sm italic">
                    {errorMessage}
                </p>
            )}
            <InputError error={error} />
        </div>
    );
};
